import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import AlertsDropdown from './AlertsDropdown';
import { alertsApi } from '../services/alertsService';
import {
  Bell,
  Home,
  CalendarDays,
  Users,
  BarChart2,
  Lock,
  Wrench,
  LogOut,
  Menu,
  Wallet
} from 'lucide-react';

const SideNav = styled.nav<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  height: 100vh;
  width: 280px;
  min-width: 280px;
  background-color: var(--surface-color);
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.1);
  transform: ${props => props.$isOpen ? 'translateX(0)' : 'translateX(100%)'};
  transition: transform 0.3s ease;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;

  @media (min-width: 1024px) {
    transform: translateX(0);
    position: sticky;
    height: 100vh;
    min-height: 100vh;
  }
`;

const NavHeader = styled.div`
  padding: 1.5rem 1.5rem 1rem;
  border-bottom: 1px solid var(--border-color);
  text-align: center;
`;

const LogoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Logo = styled.img`
  width: 100%;
  height: auto;
  max-height: 110px;
  object-fit: contain;
  border-radius: var(--border-radius-md);
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.05);
  }

  @media (max-width: 768px) {
    width: 70%;
    height: auto;
    max-height: 90px;
  }
`;

const bellShake = keyframes`
  0%, 100% { transform: rotate(0deg); }
  10%, 30%, 50%, 70%, 90% { transform: rotate(-10deg); }
  20%, 40%, 60%, 80% { transform: rotate(10deg); }
`;

const BellContainer = styled.div`
  position: relative;
  margin-top: 8px;
  display: flex;
  justify-content: flex-start;
`;

const BellButton = styled.button<{ $hasAlerts: boolean }>`
  position: relative;
  background: transparent;
  border: 2px solid var(--border-color);
  color: var(--text-primary);
  font-size: 20px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  animation: ${props => props.$hasAlerts ? bellShake : 'none'} 2s ease-in-out infinite;

  &:hover {
    background: var(--hover-background);
    border-color: var(--primary-color);
    color: var(--primary-color);
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const Badge = styled.span`
  position: absolute;
  top: -4px;
  left: -4px;
  background: var(--error-color);
  color: white;
  font-size: 11px;
  font-weight: 700;
  min-width: 20px;
  height: 20px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 5px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  animation: ${keyframes`
    0% { transform: scale(1); }
    50% { transform: scale(1.2); }
    100% { transform: scale(1); }
  `} 0.5s ease-in-out;
`;


const NavMenu = styled.ul`
  list-style: none;
  margin: 0;
  padding: 1rem 0;
  flex: 1;
`;

const NavItem = styled.li`
  margin: 0.25rem 0;
`;

const NavIcon = styled.span<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  color: ${props => props.$active ? '#008EA2' : 'var(--text-secondary)'};
  transition: color 0.2s ease;
`;

const NavLink = styled(Link)<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0.875rem 1.5rem;
  color: ${props => props.$active ? '#008EA2' : 'var(--text-primary)'};
  text-decoration: none;
  font-weight: ${props => props.$active ? '600' : '500'};
  font-size: 1rem;
  background-color: ${props => props.$active ? 'var(--hover-background)' : 'transparent'};
  border-right: 3px solid ${props => props.$active ? '#008EA2' : 'transparent'};
  transition: all 0.2s ease;

  &:hover {
    background-color: var(--hover-background);
    color: #008EA2;

    ${NavIcon} {
      color: #008EA2;
    }
  }
`;

const MenuToggle = styled.button`
  position: fixed;
  top: 1rem;
  left: 1rem;
  z-index: 1001;
  background-color: var(--primary-color);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 142, 162, 0.3);
  transition: all 0.2s ease;
  
  &:hover {
    background-color: var(--primary-hover);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 142, 162, 0.4);
  }
  
  @media (min-width: 1024px) {
    display: none;
  }
`;

const Overlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 999;
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transition: all 0.3s ease;
  
  @media (min-width: 1024px) {
    display: none;
  }
`;

const UserInfoSection = styled.div`
  padding: 1.25rem 1.5rem;
  border-top: 1px solid var(--border-color);
  text-align: right;
`;

const UserName = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 4px;
`;

const UserRole = styled.div`
  font-size: 14px;
  color: var(--text-secondary);
`;

const LogoutNavLink = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0.875rem 1.5rem;
  width: 100%;
  background: transparent;
  border: none;
  color: var(--error-color);
  font-weight: 500;
  font-size: 1rem;
  cursor: pointer;
  text-align: right;
  border-right: 3px solid transparent;
  transition: all 0.2s ease;

  &:hover {
    background-color: var(--error-light);
    border-right-color: var(--error-color);
  }

  ${NavIcon} {
    color: var(--error-color);
  }
`;

const Navigation: React.FC = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [alertsCount, setAlertsCount] = useState(0);
  const [isAlertsOpen, setIsAlertsOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const toggleNav = () => setIsOpen(!isOpen);
  const closeNav = () => setIsOpen(false);

  const loadAlertsCount = useCallback(async () => {
    if (!user) return;
    try {
      const count = await alertsApi.getUnreadCount();
      setAlertsCount(count);
    } catch (error) {
      console.error('Error loading alerts count:', error);
    }
  }, [user]);

  // Initial load
  useEffect(() => {
    loadAlertsCount();
  }, [loadAlertsCount]);

  // Polling every 30 seconds
  useEffect(() => {
    if (!user) return;

    const interval = setInterval(() => {
      // Only poll if dropdown is closed
      if (!isAlertsOpen) {
        loadAlertsCount();
      }
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [user, isAlertsOpen, loadAlertsCount]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const toggleAlerts = () => {
    setIsAlertsOpen(prev => !prev);
  };

  const closeAlerts = () => {
    setIsAlertsOpen(false);
  };

  const handleAlertsUpdated = () => {
    loadAlertsCount();
  };

  return (
    <>
      <MenuToggle onClick={toggleNav}>
        <Menu size={20} />
      </MenuToggle>
      
      <Overlay $isOpen={isOpen} onClick={closeNav} />
      
      <SideNav $isOpen={isOpen}>
        <NavHeader>
          <LogoContainer>
            <Logo
              src="/logomagialecha.png"
              alt="לוגו מגיע לך"
              loading="lazy"
            />
          </LogoContainer>

          {/* Alerts Bell Icon */}
          <BellContainer>
            <BellButton
              data-bell-button
              onClick={toggleAlerts}
              $hasAlerts={alertsCount > 0}
              title={`${alertsCount} התראות חדשות`}
            >
              <Bell size={20} />
              {alertsCount > 0 && <Badge>{alertsCount > 99 ? '99+' : alertsCount}</Badge>}
            </BellButton>
            <AlertsDropdown
              isOpen={isAlertsOpen}
              onClose={closeAlerts}
              onAlertsUpdated={handleAlertsUpdated}
            />
          </BellContainer>
        </NavHeader>
        
        <NavMenu>
          {/* לוח הבקרה */}
          {user && (
            <NavItem>
              <NavLink
                to="/dashboard"
                $active={location.pathname === '/dashboard'}
                onClick={closeNav}
              >
                <NavIcon $active={location.pathname === '/dashboard'}><Home size={20} /></NavIcon>
                לוח הבקרה
              </NavLink>
            </NavItem>
          )}
          
          {/* Lead Management — SystemAdmin, SalesRep, Secretary (Secretary רואה רק לידים מוקצים) */}
          {user && (user.permissionLevel === 1 || user.permissionLevel === 2 || user.permissionLevel === 4) && (
            <NavItem>
              <NavLink
                to="/leads"
                $active={location.pathname === '/leads'}
                onClick={closeNav}
              >
                <NavIcon $active={location.pathname === '/leads'}><CalendarDays size={20} /></NavIcon>
                לידים
              </NavLink>
            </NavItem>
          )}

          {/* Client Management — SystemAdmin, CaseManager, Secretary (Secretary רואה רק לקוחות מוקצים) */}
          {user && (user.permissionLevel === 1 || user.permissionLevel === 3 || user.permissionLevel === 4) && (
            <NavItem>
              <NavLink
                to="/clients"
                $active={location.pathname === '/clients' || location.pathname.startsWith('/clients/')}
                onClick={closeNav}
              >
                <NavIcon $active={location.pathname === '/clients' || location.pathname.startsWith('/clients/')}><Users size={20} /></NavIcon>
                לקוחות
              </NavLink>
            </NavItem>
          )}

          {/* Reports — SystemAdmin (full) and Secretary (filtered to her scope) */}
          {user && (user.permissionLevel === 1 || user.permissionLevel === 4) && (
            <NavItem>
              <NavLink
                to="/reports"
                $active={location.pathname === '/reports'}
                onClick={closeNav}
              >
                <NavIcon $active={location.pathname === '/reports'}><BarChart2 size={20} /></NavIcon>
                דוחות
              </NavLink>
            </NavItem>
          )}

          {/* User management — SystemAdmin */}
          {user && user.permissionLevel === 1 && (
            <NavItem>
              <NavLink
                to="/users"
                $active={location.pathname === '/users'}
                onClick={closeNav}
              >
                <NavIcon $active={location.pathname === '/users'}><Lock size={20} /></NavIcon>
                משתמשים
              </NavLink>
            </NavItem>
          )}

          {/* Only System Admins see system settings */}
          {user && user.permissionLevel === 1 && (
            <NavItem>
              <NavLink
                to="/system-settings"
                $active={location.pathname === '/system-settings'}
                onClick={closeNav}
              >
                <NavIcon $active={location.pathname === '/system-settings'}><Wrench size={20} /></NavIcon>
                הגדרות מערכת
              </NavLink>
            </NavItem>
          )}

          {/* ניהול תשלומים — תפקיד (מנהל מערכת / מזכירה) */}
          {user && (user.permissionLevel === 1 || user.permissionLevel === 4) && (
            <NavItem>
              <NavLink
                to="/payments-management"
                $active={location.pathname === '/payments-management'}
                onClick={closeNav}
              >
                <NavIcon $active={location.pathname === '/payments-management'}><Wallet size={20} /></NavIcon>
                ניהול תשלומים
              </NavLink>
            </NavItem>
          )}

          {/* כפתור התנתקות */}
          <NavItem>
            <LogoutNavLink onClick={handleLogout}>
              <NavIcon><LogOut size={20} /></NavIcon>
              התנתקות
            </LogoutNavLink>
          </NavItem>
        </NavMenu>

        {/* פרטי משתמש בתחתית */}
        <UserInfoSection>
          <UserName>{user?.fullName}</UserName>
          <UserRole>{user?.permissionLevelName}</UserRole>
        </UserInfoSection>
      </SideNav>
    </>
  );
};

export default Navigation;